import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SelectWithValidation from '../../../../components/ValidationFormElements/SelectWithValidation';
import ArmenianRegionSelectOptions
from '../../../../components/FormComponents/ContactInfo/ArmenianRegionSelectOptions';
import TextInputFieldWithValidation
from '../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import FormSection from '../../../../components/FormComponents/FormSection';
import { getMenuLabelWithTranslation } from '../../../../helpers/selectHelpers';
import { selectSortedRAStates, selectSortedCityVillages } from '../../../../redux/modules/countries';
import { changeAccidentState } from '../../../../helpers/claims';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import { findIsYerevan, isArmenianCountry } from '../../../../helpers/countryHelper';
import { handleAccidentCountryChange } from '../../../../helpers/claims/claimApplicationHelpers';
import { ACC_INITIAL_ADDRESS, CLAIM_TYPES } from '../../../../constants';

import AccidentPlaceIcon from '../../../../img/formSectionIcons/accidentPlace.svg';

const AccidentAddress = ({ formProps, accidentInfo, countries, claimType }) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const RAStates = useSelector((state) => selectSortedRAStates(state, i18n.language));
  const accCityVillages = useSelector((state) => selectSortedCityVillages(state, i18n.language));

  const { errors, clearError, register, getValues, setValue } = formProps;
  const [addressStreetRequired, setAddressStreetRequireq] = useState(true);

  useRegisterValidationFields([
    'accCountry',
    'isAccInArmenia',
    'accState',
    'accVillage',
  ], register);

  const [accAddress, setAccAddress] = useState(ACC_INITIAL_ADDRESS);

  useEffect(() => {
    if (accidentInfo.accCountry) {
      setAccAddress({
        isAccInArmenia: isArmenianCountry(accidentInfo.accCountry),
        accCountry: accidentInfo.accCountry,
        accState: accidentInfo.accState,
        accVillage: accidentInfo.accVillage,
      });
    }
  }, [accidentInfo]);

  const [accVillages, setAccVillages] = useState([]);
  const accIsYerevan = useMemo(() => findIsYerevan(accAddress.accState), [accAddress.accState]);

  const getLabelWithTranslation = useCallback(
    (option) => getMenuLabelWithTranslation(option, i18n),
    [i18n],
  );

  useEffect(() => {
    setAccVillages(accCityVillages);
  }, [accCityVillages]);

  const handleAccCountryChange = useCallback((accCountry) => {
    setAddressStreetRequireq(!(accCountry.iso3 === 'ARM' && claimType === CLAIM_TYPES.TRAVEL));
    handleAccidentCountryChange(
      accCountry,
      setAccAddress,
      setValue,
      setAccVillages,
    );
  }, [claimType, setValue]);

  const handleAccVillageChange = (accVillage) => {
    setAccAddress({
      ...accAddress,
      accVillage,
    });
    setValue('accVillage', accVillage, true);
  };

  const handleAccStateChange = (accState) => {
    changeAccidentState(
      setAccAddress,
      accAddress,
      accState,
      setValue,
      dispatch,
    );
  };

  const handleStreetChange = (target) => {
    setValue(target.name, target.value, true);
  };

  return (
    <FormSection title="claim.accInfo.accAddress" icon={AccidentPlaceIcon}>
      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
          md={!isEmpty(accAddress.accState) && accAddress.isAccInArmenia && !accIsYerevan ? 4 : 6}
        >
          <SelectWithValidation
            formProps={{
              errors,
              clearError,
            }}
            value={accAddress.accCountry || ''}
            options={countries}
            getMenuLabel={getLabelWithTranslation}
            onChange={handleAccCountryChange}
            label="insurance.country"
            required
            name="accCountry"
          />
        </Grid>
        {accAddress.isAccInArmenia && (
          <ArmenianRegionSelectOptions
            cityVillages={accVillages}
            isYerevan={accIsYerevan}
            onCityVillageChange={handleAccVillageChange}
            errors={errors}
            clearError={clearError}
            states={RAStates}
            form={{
              cityVillage: {
                value: accAddress.accVillage || {},
                label: 'contactInfo.cityVillage',
                name: 'accVillage',
              },
              state: {
                value: accAddress.accState || {},
                label: 'contactInfo.state',
                name: 'accState',
              },
            }}
            onStateChange={handleAccStateChange}
          />
        )}
        <Grid item xs={12}>
          <TextInputFieldWithValidation
            formProps={{ errors, clearError }}
            labelKey={isArmenianCountry(getValues().accCountry) ? 'claim.accInfo.addressStreet' : 'claim.accInfo.addressCity'}
            name="accStreet"
            handleChange={(e) => handleStreetChange(e.target)}
            register={register}
            required={addressStreetRequired}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

AccidentAddress.propTypes = {
  formProps: PropTypes.shape({
    errors: PropTypes.shape({}),
    clearError: PropTypes.func,
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
  }),
  accidentInfo: PropTypes.shape({
    accCountry: PropTypes.shape({ iso3: PropTypes.string }),
    accVillage: PropTypes.shape({}),
    accState: PropTypes.shape({}),
    accStreet: PropTypes.string,
  }),
  countries: PropTypes.arrayOf(PropTypes.shape({})),
  claimType: PropTypes.string,
};

AccidentAddress.defaultProps = {
  accidentInfo: {},
  countries: [],
  formProps: {},
  claimType: null,
};

export default AccidentAddress;
